import React, { Component } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuSave, LuTriangleAlert } from "react-icons/lu";
import AppModal from "../../common/modal/AppModal";
import RatioPicker from "../../nav_bar/global_config/RatioPicker";
import { _saveAs } from "../../../../common/server_utils.es6";
import {
  getValueFromPreset,
  getPresetFromValue,
  DEFAULT_RATIO_KEY,
  DEFAULT_RATIO_VALUE,
} from "../../../../core/editor/ratio_presets";
import { migrateBoxesForRatio } from "../../../../common/aspect_ratio_migration";
import "./_saveAsModal.scss";

const RATIO_EPSILON = 1e-6;

/**
 * SaveAs modal — modern AppModal-based redesign.
 *
 * Always offers a RatioPicker so the user can swap aspect ratio at the SaveAs
 * step even when the parent didn't initiate a ratio change. When the resulting
 * ratio differs from the one the modal was opened with, we surface an explicit
 * warning ("scene elements may not fit perfectly") and re-run
 * migrateBoxesForRatio on whichever base state is being saved.
 */
export default class SaveAsModal extends Component {
  constructor(props) {
    super(props);
    const initialKey = props.initialRatioKey || getPresetFromValue(props.initialCanvasRatio);
    const initialValue =
      typeof props.initialCanvasRatio === "number" && isFinite(props.initialCanvasRatio)
        ? props.initialCanvasRatio
        : initialKey
          ? getValueFromPreset(initialKey) || DEFAULT_RATIO_VALUE
          : DEFAULT_RATIO_VALUE;
    this.state = {
      trainingName: props.preparedTitle || props.title || "",
      trainingDescription: "",
      selectedRatioKey: initialKey || DEFAULT_RATIO_KEY,
      selectedCanvasRatio: initialValue,
    };
    this.toggleModal = this.toggleModal.bind(this);
  }

  componentDidMount() {
    window.addEventListener("keyup", this.toggleModal);
  }

  componentDidUpdate(prevProps) {
    // When the modal becomes visible, re-seed the form from the latest props.
    if (this.props.visible && !prevProps.visible) {
      const initialKey =
        this.props.initialRatioKey || getPresetFromValue(this.props.initialCanvasRatio);
      const initialValue =
        typeof this.props.initialCanvasRatio === "number" && isFinite(this.props.initialCanvasRatio)
          ? this.props.initialCanvasRatio
          : initialKey
            ? getValueFromPreset(initialKey) || DEFAULT_RATIO_VALUE
            : DEFAULT_RATIO_VALUE;
      this.setState({
        trainingName: this.props.preparedTitle || this.props.title || "",
        trainingDescription: "",
        selectedRatioKey: initialKey || DEFAULT_RATIO_KEY,
        selectedCanvasRatio: initialValue,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keyup", this.toggleModal);
  }

  toggleModal(e) {
    const key = e.keyCode ? e.keyCode : e.which;
    if (key === 27 && this.props.visible) {
      this.props.onSaveAsModalToggled();
    }
  }

  handleRatioChange = (key) => {
    const value = getValueFromPreset(key);
    if (typeof value === "number") {
      this.setState({ selectedRatioKey: key, selectedCanvasRatio: value });
    }
  };

  /**
   * Compose the state to ship to `_saveAs`. Starts from the parent-provided
   * base (`preparedState` for chained flows, otherwise `exportedState`) and
   * re-runs the box migration whenever the user picked a different ratio at
   * the SaveAs step.
   */
  buildPayloadState = () => {
    const baseState = this.props.preparedState || this.props.exportedState;
    if (!baseState) return baseState;

    const baseCanvasRatio =
      typeof this.props.initialCanvasRatio === "number" && isFinite(this.props.initialCanvasRatio)
        ? this.props.initialCanvasRatio
        : DEFAULT_RATIO_VALUE;
    const targetCanvasRatio = this.state.selectedCanvasRatio;
    const ratioChanged =
      Math.abs(baseCanvasRatio - targetCanvasRatio) > RATIO_EPSILON &&
      typeof targetCanvasRatio === "number";

    if (!ratioChanged) return baseState;

    // baseState may be wrapped ({ undoGroup: { present } }) or a flat present.
    const present = baseState && baseState.undoGroup ? baseState.undoGroup.present : baseState;
    if (!present) return baseState;

    const migratedPresent = migrateBoxesForRatio(present, baseCanvasRatio, targetCanvasRatio);
    const newGlobalConfig = {
      ...(migratedPresent.globalConfig || {}),
      canvasRatio: targetCanvasRatio,
      ratio: this.state.selectedRatioKey,
    };
    const nextPresent = { ...migratedPresent, globalConfig: newGlobalConfig };

    if (baseState && baseState.undoGroup) {
      return { ...baseState, undoGroup: { ...baseState.undoGroup, present: nextPresent } };
    }
    return nextPresent;
  };

  ratioWasChangedFromBase = () => {
    const baseCanvasRatio =
      typeof this.props.initialCanvasRatio === "number" && isFinite(this.props.initialCanvasRatio)
        ? this.props.initialCanvasRatio
        : DEFAULT_RATIO_VALUE;
    return Math.abs(baseCanvasRatio - this.state.selectedCanvasRatio) > RATIO_EPSILON;
  };

  handleSave = () => {
    const payload = this.buildPayloadState();
    _saveAs(this.state.trainingName, this.state.trainingDescription, payload);
    this.props.onSaveAsModalToggled();
  };

  render() {
    const ratioChanged = this.ratioWasChangedFromBase();
    const ratioWarning = i18n.t("save_as_modal_ratio_change_warning", {
      ratio: this.state.selectedRatioKey,
      defaultValue: `Same content will be saved at ${this.state.selectedRatioKey} aspect ratio. When the aspect ratio changes, scene elements may not fit perfectly.`,
    });

    return (
      <AppModal
        show={!!this.props.visible}
        onClose={this.props.onSaveAsModalToggled}
        title={i18n.t("save_as_modal") || "Save As"}
        size="lg"
        className="saveAsModal"
        dialogClassName="save-as-modal__dialog"
        bodyClassName="save-as-modal__body"
        bodyProps={{ className: "save-as-modal__body" }}
        footerProps={{ className: "save-as-modal__footer" }}
        cancelLabel={i18n.t("Cancel") || "Cancel"}
        onCancel={this.props.onSaveAsModalToggled}
        confirmLabel={
          <>
            <LuSave aria-hidden="true" />
            <span>{i18n.t("marks.save_changes") || "Save"}</span>
          </>
        }
        onConfirm={this.handleSave}
      >
        <div className="save-as-modal__form">
          <div className="save-as-modal__field">
            <label htmlFor="save-as-name" className="save-as-modal__label">
              {i18n.t("TrainingName") || "Training name"}
            </label>
            <input
              id="save-as-name"
              type="text"
              className="save-as-modal__input"
              value={this.state.trainingName}
              onChange={(e) => this.setState({ trainingName: e.target.value })}
            />
          </div>

          <div className="save-as-modal__field">
            <label htmlFor="save-as-desc" className="save-as-modal__label">
              {i18n.t("TrainingDescription") || "Description"}
            </label>
            <textarea
              id="save-as-desc"
              className="save-as-modal__input save-as-modal__textarea"
              rows={4}
              value={this.state.trainingDescription}
              onChange={(e) => this.setState({ trainingDescription: e.target.value })}
            />
          </div>

          <div className="save-as-modal__field">
            <label className="save-as-modal__label">
              {i18n.t("save_as_modal_aspect_ratio") || "Aspect ratio"}
            </label>
            <RatioPicker
              selectedKey={this.state.selectedRatioKey}
              onChange={this.handleRatioChange}
            />
          </div>

          {ratioChanged && (
            <div className="save-as-modal__warning" role="alert">
              <LuTriangleAlert aria-hidden="true" className="save-as-modal__warning-icon" />
              <span>{ratioWarning}</span>
            </div>
          )}
        </div>
      </AppModal>
    );
  }
}

SaveAsModal.propTypes = {
  visible: PropTypes.any.isRequired,
  onSaveAsModalToggled: PropTypes.any.isRequired,
  title: PropTypes.string,
  /**
   * Selected contained view (kept for backwards-compatible call-sites).
   */
  containedViewSelected: PropTypes.any,
  containedViews: PropTypes.object,
  navItems: PropTypes.object,
  navItemSelected: PropTypes.any,
  onIndexSelected: PropTypes.func,
  indexSelected: PropTypes.any,
  /**
   * Pre-built state to save (used by the ratio-change SaveAs chain). Falls
   * back to `exportedState` when null.
   */
  preparedState: PropTypes.object,
  /**
   * Live exported state used when the modal is opened directly (not chained
   * from GlobalConfig).
   */
  exportedState: PropTypes.object,
  /**
   * Pre-fill value for the training-name field.
   */
  preparedTitle: PropTypes.string,
  /**
   * Aspect ratio the modal opens with — used to seed the RatioPicker and to
   * decide whether the user later changed the ratio.
   */
  initialCanvasRatio: PropTypes.number,
  initialRatioKey: PropTypes.string,
};
